import { LogoCell, LogoCellType } from './LogoCell.js';
import { WebsiteCell, WebsiteCellType } from '../Shared/TableCells/WebsiteCell.js';
import { ContactCell, ContactCellFilter } from '../Shared/TableCells/ContactCell.js';
import { loadUserColumnWidths } from './StationsPageTableCachingUtils.js';
import { getPlatform } from "../../utilities/orientationAndPlatform.js";
import { getGridStringOperators } from '@mui/x-data-grid';


export function getColumnHeadersBasic(){
    
    const platform = getPlatform()
    let textAlign = 'center'
    let callSignWidth = 60
    let affiliationWidth = 60
    let flexes = [4, 2, 4, 4]
    let logoWidth = 200
    if (platform === 'mobile') {
        flexes = [3, 3.3, 3, 3]
        logoWidth = 110
    }
    
    const columnHeadersBasic = [
        {
            field: 'logo', 
            ...LogoCellType, 
            headerName: 'Logo',
            renderCell: (params) => {
                if(!params.row) return null;
                return <LogoCell {...params} isLive={params.row.isLive}/>;
            },
            description: 'Click on a station\'s logo to go to their page', 
            sortable: false,
            align: textAlign,
            headerAlign: 'center',
            headerClassName: 'stationsPageTableColumnHeaders',
            width: logoWidth,
        },
        {
            field: 'call_sign', 
            headerName: 'Call Sign', 
            type: 'string', 
            sortable: false,
            description: 'Call sign for the station',
            align: textAlign,
            headerAlign: 'center',
            headerClassName: 'stationsPageTableColumnHeaders',
            ...(platform === 'mobile'
            ? { width: callSignWidth }
            : { flex: flexes[1] }),
        },
        {
            field: 'affiliation', 
            headerName: 'Affiliate', 
            type: 'string', 
            sortable: false,
            description: 'Station affiliate',
            align: textAlign,
            headerAlign: 'center',
            headerClassName: 'stationsPageTableColumnHeaders',
            ...(platform === 'mobile'
            ? { width: affiliationWidth }
            : { flex: flexes[2] }),
        },
        {
            field: 'market',
            headerName: 'Market',
            type: 'string',  
            sortable: false,
            description: 'Station Market',
            align: textAlign,
            headerAlign: 'center',
            headerClassName: 'stationsPageTableColumnHeaders',
            flex: flexes[3]
        },
    ]
    return columnHeadersBasic
}


export function getColumnHeadersAdvanced(){  

    let columnWidths = loadUserColumnWidths()
    
    const columnHeadersAdvanced = [
        {
            field: 'logo', 
            ...LogoCellType, 
            headerName: 'Logo', 
            renderCell: (params) => <LogoCell {...params} />,
            width: columnWidths.logo,
            description: 'Click on a station\'s logo to go to their page', 
            sortable: false,
            headerAlign: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'call_sign', 
            headerName: 'Call Sign', 
            type: 'string', 
            width: columnWidths.call_sign,
            description: 'Call sign for the station',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'affiliation', 
            headerName: 'Affiliate', 
            type: 'string', 
            width: columnWidths.affiliation,
            description: 'Station affiliate',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'market',
            headerName: 'Market',
            type: 'string', 
            width: columnWidths.market, 
            description: 'Station Market',
            headerAlign: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'sinclair_station_status', 
            headerName: 'Sinclair Station Status', 
            type: 'string', 
            width: columnWidths.sinclair_station_status,
            description: 'Sinclair Station Status',
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'dma_code', 
            headerName: 'DMA Code', 
            type: 'number', 
            width: columnWidths.dma_code,
            description: 'DMA Code',
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'website_vod_assets', 
            ...WebsiteCellType, 
            headerName: 'Website', 
            renderCell: (params) => <WebsiteCell {...params} />,
            width: columnWidths.website_vod_assets,
            description: 'Website', 
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'host_or_non_host_for_atsc3', 
            headerName: 'Host or Non-Host For ATSC3', 
            type: 'boolean', 
            width: columnWidths.host_or_non_host_for_atsc3,
            description: 'Station Host Status For ATSC3',
            headerClassName: 'stationsPageTableColumnHeaders'
        },


        //hidden by default currently
        {
            field: 'ba_blackout_log_types',
            headerName: 'Log Types For BA Blackout',
            type: 'string',
            width: columnWidths.ba_blackout_log_types,
            description: 'What log types the BA Blackout currently supports for the station',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {            
            field: 'ba_blackout_log_file_name',
            headerName: 'BA Blackout Log File Name',
            type: 'string',
            width: columnWidths.ba_blackout_log_file_name,
            description: 'The BA Blackout log file name for the station',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'wide_orbit_log_recieved_format',
            headerName: 'Wide Orbit Log Recieved Format',
            type: 'string',
            width: columnWidths.wide_orbit_log_recieved_format,
            description: 'Wide Orbit log recieved format',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'wide_orbit_file_type',
            headerName: 'Wide Orbit File Type',
            type: 'string',
            width: columnWidths.wide_orbit_file_type,
            description: 'Wide Orbit log file type',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'receiving_daily_WO_logs',
            headerName: 'Receiving Daily WO Logs?',
            type: 'boolean',
            width: columnWidths.receiving_daily_WO_logs,
            description: 'Receiving Daily WO Logs?',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {            
            field: 'atsc3_deployed',
            headerName: 'ATSC3 Deployment Status',
            type: 'boolean',
            width: columnWidths.atsc3_deployed,
            description: 'Is ATSC3 deployed for the station?',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'atsc3_deployment_version',
            headerName: 'ATSC3 Deployment Version',
            type: 'string',
            width: columnWidths.atsc3_deployment_version,
            description: 'ATSC3 Deployment Version',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'atsc1_vp1_deployed',
            headerName: 'ATSC1 Deployment Status',
            type: 'boolean',
            width: columnWidths.atsc1_vp1_deployed,
            description: 'Is ATSC1 deployed for the station?',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'atsc3_gsid',
            headerName: 'ATSC3 GSID',
            type: 'string',
            width: columnWidths.atsc3_gsid,
            description: '3.0 GSID',
            headerAlign: 'center',
            headerClassName: 'stationsPageTableColumnHeaders',
        },
        {
            field: 'digicaster_ip_addresses',
            headerName: 'Digicaster IP Addresses',
            type: 'string',
            width: columnWidths.digicaster_ip_addresses,
            description: 'Digicaster IP Addresses',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'google_analytics_deployment_verified',
            headerName: 'Google Analytics Deployment Verified',
            type: 'boolean',
            width: columnWidths.google_analytics_deployment_verified,
            description: 'Google Analytics Deployment Verified',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'time_zone',
            headerName: 'Time Zone',
            type: 'string',
            width: columnWidths.time_zone,
            description: 'Time Zone',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'notes',
            headerName: 'Notes',
            type: 'string',
            width: columnWidths.notes,
            description: 'notes',
            headerAlign: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'bsid3_or_tsid1_verance_required_info',
            headerName: 'BSID (3.0) or TSID (1.0) Verance Required Info',
            type: 'string',
            width: columnWidths.bsid3_or_tsid1_verance_required_info,
            description: 'Verance Required Info',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'major_minor_virtual_channel_number',
            headerName: 'Major Minor Virtual Channel Number',
            type: 'string',
            width: columnWidths.major_minor_virtual_channel_number,
            description: 'Major Minor Virtual Channel Number',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'verance_service_id',
            headerName: 'Verance Service ID',
            type: 'string',
            width: columnWidths.verance_service_id,
            description: 'Verance Service ID',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'signal_signed',
            headerName: 'Signal Signed',
            type: 'boolean',
            width: columnWidths.signal_signed,
            description: 'Signal Signed',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'site_content',
            headerName: 'Site Content',
            type: 'string',
            width: columnWidths.site_content,
            description: 'Site Content',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'headlines_newsletter',
            headerName: 'Headlines Newsletter',
            type: 'boolean',
            width: columnWidths.headlines_newsletter,
            description: 'Headlines Newsletter',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'news_app',
            headerName: 'News App',
            type: 'boolean',
            width: columnWidths.news_app,
            description: 'News App',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'weather_newsletter',
            headerName: 'Weather Newsletter',
            type: 'boolean',
            width: columnWidths.weather_newsletter,
            description: 'Weather Newsletter',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'weather_app',
            headerName: 'Weather App',
            type: 'string',
            width: columnWidths.weather_app,
            description: 'Weather App',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
        {
            field: 'station_contacts', 
            headerName: 'Contacts', 
            filterOperators: getGridStringOperators().filter((operator) => operator.value !== 'equals'),
            valueGetter: ContactCellFilter,
            renderCell: (params) => <ContactCell {...params} />,
            width: columnWidths.station_contacts,
            description: 'Contacts', 
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'stationsPageTableColumnHeaders'
        },
    ];
    return columnHeadersAdvanced;
}
